import {
    Component,
    ChangeDetectionStrategy,
    Input,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    AfterViewInit,
    ViewChild,
    ElementRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { HtmlInputNumber } from '@panaya/ui/pipes';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { ButtonTypeEnum } from '@panaya/ui/p-button';
import { IconName } from '@panaya/ui/p-icon';
import * as _ from 'lodash';

@Component({
    selector: 'p-field-auto-complete',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [isReadOnly]="isReadOnly"
            [customClasses]="customClasses"
        >
            <div class="p-input-text p-paragraph-1" slot="readMode" floatLabel="always">
                <div fxFlex fxLayout="row" [matTooltip]="value ?? ''">
                    <ng-content select="[slot=readMode]"></ng-content>
                    <div class="mat-mdc-form-field-infix text-format">
                        <p-button
                            *ngIf="iconBefore"
                            class="icon-before"
                            matPrefix
                            [buttonType]="buttonType"
                            [iconBefore]="iconBefore"
                            iconBeforeSize="sm"
                            (buttonClicked)="buttonClicked.emit($event)"
                        ></p-button>
                        {{ value }}
                    </div>
                </div>
            </div>
            <mat-form-field
                class="p-input-text p-paragraph-1"
                slot="editMode"
                appearance="fill"
                floatLabel="always"
                [matTooltip]="value ?? ''"
            >
                <ng-content select="[slot=editMode]" fxFlex="20"></ng-content>
                <p-button
                    *ngIf="iconBefore"
                    class="icon-before"
                    matPrefix
                    [buttonType]="buttonType"
                    [iconBefore]="iconBefore"
                    iconBeforeSize="sm"
                    (buttonClicked)="buttonClicked.emit($event)"
                ></p-button>
                <input
                    #pInput
                    class="text-format"
                    matInput
                    fxFlex
                    [attr.maxlength]="maxLength"
                    type="text"
                    (input)="changeInputValue($event)"
                    [value]="value"
                    [matAutocomplete]="auto"
                />
            </mat-form-field>
            <mat-autocomplete #auto="matAutocomplete" (optionSelected)="changeValue($event.option.value)">
                <mat-option *ngFor="let value of filteredValues" [value]="value" (mousedown)="optionMouseDown.emit($event)">
                    {{ value }}
                </mat-option>
                <mat-option class="no-data" disabled *ngIf="isValueNotIncludedInValues()">
                    {{ noData }}
                </mat-option>
            </mat-autocomplete>
        </p-field>
    `,
    styleUrls: ['./p-field-auto-complete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldAutoCompleteComponent
        }
    ]
})
export class PFieldAutoCompleteComponent extends PFieldAbstractComponent<string> implements AfterViewInit {
    buttonType = ButtonTypeEnum.IsIcon;

    @Input() label?: string;
    @Input() isMandatory = false;
    @Input() override isReadOnly = false;
    @Input() isInEditMode = true;
    @Input() customClasses?: string;
    @Input() override isDisabled = false;
    @Input() values: string[] = [];
    @Input() noData = 'NO DATA FOUND';
    @Input() minCharacters: number = 2;
    @Input() iconBefore?: IconName;
    @Input() public autoFocus = false;
    @Input('maxlength') public maxLength?: HtmlInputNumber;

    @Output() optionMouseDown = new EventEmitter<MouseEvent>();
    @Output() buttonClicked = new EventEmitter<any>();

    @ViewChild('pInput') private readonly pInput?: ElementRef<HTMLInputElement>;

    filteredValues: string[] = [];

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    changeInputValue(event: any) {
        if (this.textContainsOnlySpaces(event.target?.value)) {
            event.target.value = '';
            return;
        }
        this.markAsTouched();
        if (!this.isDisabled) {
            this.filteredValues = _.size(event.target?.value) >= this.minCharacters ? this._filter(event.target?.value) : [];
            this.changeValue(event.target?.value);
        }
    }

    textContainsOnlySpaces(text: string): 0 | undefined | boolean {
        return text?.length && !text?.trim().length;
    }

    changeValue(value: any) {
        this.value = value;
        this._onChange(value);
    }

    private _filter(value: string): string[] {
        const filterValue = this._normalizeValue(value);
        return this.values.filter((val) => _.startsWith(this._normalizeValue(val), filterValue));
    }

    private _normalizeValue(value: string): string {
        return value.toLowerCase().replace(/\s/g, '');
    }

    ngAfterViewInit(): void {
        if (this.autoFocus) {
            setTimeout(() => this.pInput?.nativeElement.focus());
        }
    }

    protected isValueNotIncludedInValues(): boolean {
        return (
            !this.filteredValues?.length &&
            !!this.value?.length &&
            !!(this.value?.length >= this.minCharacters) &&
            !this.values.includes(this.value)
        );
    }
}
